import React from 'react';
import { clsx } from 'clsx';
import type { AIBudget } from '@/types/admin';
import { BUDGET_SCOPE_LABELS } from '@/types/admin';
import { formatUSD } from '@/utils/formatters';

const STATE: Record<string, { label: string; bar: string; badge: string }> = {
  ok: { label: 'Dentro do orçamento', bar: 'bg-emerald-500', badge: 'badge-success' },
  alert: { label: 'Alerta', bar: 'bg-amber-500', badge: 'badge-warning' },
  blocked: { label: 'Bloqueado', bar: 'bg-red-600', badge: 'badge-danger' },
  exceeded: { label: 'Acima do teto (sem bloqueio)', bar: 'bg-red-500', badge: 'badge-danger' },
  unlimited: { label: 'Sem teto', bar: 'bg-slate-400', badge: 'badge-neutral' },
};

/** Barra de consumo do mês contra o teto (verde → amarelo no alerta → vermelho no teto). */
export const BudgetBar: React.FC<{ budget: AIBudget }> = ({ budget }) => {
  const st = STATE[budget.state] ?? STATE.ok;
  const pct = Math.min(100, budget.percent);
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="font-medium text-slate-800">{BUDGET_SCOPE_LABELS[budget.scope] ?? budget.scope}</span>
        <span className={st.badge}>{st.label}</span>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden relative">
        {budget.monthly_limit_usd !== null && (
          <>
            <div className={clsx('h-full rounded-full transition-all', st.bar)} style={{ width: `${pct}%` }} />
            {budget.alert_percent > 0 && budget.alert_percent < 100 && (
              <div className="absolute top-0 bottom-0 w-px bg-slate-500/60" style={{ left: `${budget.alert_percent}%` }} title="Alerta" />
            )}
          </>
        )}
      </div>
      <div className="text-small text-slate-500">
        {formatUSD(budget.spent_usd, 2)} gastos no mês
        {budget.monthly_limit_usd !== null
          ? ` de ${formatUSD(budget.monthly_limit_usd, 2)} (${budget.percent.toLocaleString('pt-BR')}%)${budget.hard_block ? ' · bloqueia ao atingir 100%' : ' · só avisa'}`
          : ' · sem teto definido'}
      </div>
    </div>
  );
};
